import PropTypes from 'prop-types';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
//
import { fileData } from '../file-thumbnail';

// ----------------------------------------------------------------------

export default function RejectionFiles({ fileRejections = [], customRejections = [] }) {
  // Merge both rejection arrays
  const allRejections = [...fileRejections, ...customRejections];
  
  if (!allRejections.length) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        mt: 3,
        textAlign: 'left',
        borderStyle: 'dashed',
        borderColor: 'error.main',
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
      }}
    >
      {allRejections.map(({ file, errors }) => {
        const { path } = fileData(file);

        // Prioritize file type errors over size errors
        // If file type error exists, only show that; otherwise show size error
        const fileTypeError = errors.find(error => error.code === 'file-invalid-type');
        const sizeError = errors.find(error => error.code === 'file-too-large');
        const durationError = errors.find(error => error.code === 'file-too-long' || error.code === 'duration-exceeded');
        
        // Format error messages
        const formatError = (error) => {
          // Check if file is a video or image to use appropriate label
          const isVideo = file.type?.startsWith('video/') || 
                         file.name?.toLowerCase().match(/\.(mp4|mov|avi|webm|m4v|wmv)$/);
          const isImage = file.type?.startsWith('image/') || 
                         file.name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/);
          
          if (error.code === 'file-too-large') {
            // Format the error message - replace bytes with appropriate size based on file type
            if (error.message && (error.message.includes('bytes') || error.message.includes('31457280'))) {
              if (isVideo) {
                return {
                  ...error,
                  message: 'Video is larger than 30MB'
                };
              } else if (isImage) {
                return {
                  ...error,
                  message: 'Image is larger than 3MB'
                };
              }
              return {
                ...error,
                message: 'File is larger than 30MB'
              };
            }
            // If message already contains "File", update to "Video" or "Image" based on file type
            if (error.message && error.message.includes('File is larger than')) {
              if (isVideo) {
                return {
                  ...error,
                  message: error.message.replace('File is larger than', 'Video is larger than')
                };
              } else if (isImage) {
                return {
                  ...error,
                  message: error.message.replace('File is larger than', 'Image is larger than')
                };
              }
            }
          } else if (error.code === 'file-too-long' || error.code === 'duration-exceeded') {
            return {
              ...error,
              message: isVideo ? 'Video is longer than 30 Sec' : 'File is longer than 30 Sec'
            };
          }
          return error;
        };
        
        const displayErrors = fileTypeError 
          ? [{ code: 'file-invalid-type', message: 'File type must be image' }]
          : sizeError && durationError
            ? [formatError(sizeError), formatError(durationError)]
            : sizeError 
              ? [formatError(sizeError)]
              : durationError
                ? [formatError(durationError)]
                : errors.map(formatError);

        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {path}
            </Typography>

            {displayErrors.map((error) => (
              <Box key={error.code} component="span" sx={{ typography: 'caption', display: 'block' }}>
                {error.message}
              </Box>
            ))}
          </Box>
        );
      })}
    </Paper>
  );
}

RejectionFiles.propTypes = {
  fileRejections: PropTypes.array,
  customRejections: PropTypes.array,
};
